import { useMemo, useState } from 'react';
import {
  PROJECT_COLORS,
  STATUS_COLORS,
  type Skill,
  type SkillCategory,
  type SkillLevel,
} from '@forecastify/shared';
import { Select, Tabs, TextInput, type MenuItem } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { ColorPicker } from '../delivery/color-picker';
import { EditorDialog } from './editor-dialog';
import { SettingsPage } from './settings-page';
import { Field } from '../card';
import { useDragOrder } from './settings-shell';
import { SettingsTable, type SettingsRow } from './settings-table';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Skill menedzsment. C54.                                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Three tabs over three reorderable lists — **Skillek**, **Kategóriák**, **Szintek** — each row
 * carrying a use count and a delete the count explains.
 *
 * ⚠️⚠️ **THE SZINTEK TAB IS NOT A THIRD LIST OF NAMES: ITS ORDER IS THE SCALE.** The mockup says
 * so in its own comment — *"a szintek sorrendje adja a súlyt: az első a leggyengébb"* — so the row
 * shows its **rank** and the drag handle is doing something different here than on the other two
 * tabs. Dragging *Mentor* above *Kezdő* re-sorts every person's skill list in the product. The
 * note under the list says that, because nothing else on the screen would.
 *
 * ⚠️ **Reordering is a grabber plus the row menu's move items**, which is `useDragOrder`'s whole
 * reason for existing: an HTML5 drag is invisible to a keyboard, and the menu is the path that
 * is not.
 *
 * ⚠️ **No width cap** — round 5 item 2. Nine settings screens capped themselves and moved the
 * table under the reader between them.
 */

type Tab = 'skills' | 'categories' | 'levels';

/** The colour a new category starts with — the palette's indigo, `#525EC6`. */
const DEFAULT_CATEGORY_COLOR = PROJECT_COLORS[1].value;
/** The colour a new level starts with — the status palette's blue, `#4EA7FC`. */
const DEFAULT_LEVEL_COLOR = STATUS_COLORS[1];

/** ⚠️ One draft shape per tab, so a row's name and its kebab cannot open two different editors. */
export interface SkillDraft {
  readonly id: string | null;
  readonly name: string;
  readonly categoryId: string;
  readonly description: string;
  readonly version: number;
}

export interface CategoryDraft {
  readonly id: string | null;
  readonly name: string;
  readonly color: string;
  readonly version: number;
}

export interface LevelDraft {
  readonly id: string | null;
  readonly name: string;
  readonly color: string;
  readonly description: string;
  readonly version: number;
}

export interface SkillsSectionProps {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly skills: readonly Skill[];
  readonly categories: readonly SkillCategory[];
  readonly levels: readonly SkillLevel[];
  readonly canManage: boolean;
  readonly serverError: string | null;

  readonly onSaveSkill: (draft: SkillDraft) => Promise<boolean>;
  readonly onDeleteSkill: (skill: Skill) => void;
  readonly onReorderSkills?: (ids: readonly string[]) => void;

  readonly onSaveCategory: (draft: CategoryDraft) => Promise<boolean>;
  readonly onDeleteCategory: (category: SkillCategory) => void;
  readonly onReorderCategories?: (ids: readonly string[]) => void;

  readonly onSaveLevel: (draft: LevelDraft) => Promise<boolean>;
  readonly onDeleteLevel: (level: SkillLevel) => void;
  readonly onReorderLevels?: (ids: readonly string[]) => void;
}

/**
 * ⚠️ **The two move items are the keyboard path**, and they are the only reason a row menu exists
 * on a list whose row already opens its own editor.
 */
function moveItems(
  t: Translate,
  name: string,
  index: number,
  total: number,
  onMove: (to: number) => void,
): readonly MenuItem[] {
  if (total < 2) return [];
  const items: MenuItem[] = [];
  if (index > 0) {
    items.push({
      label: t('settings.moveUp', { name }),
      icon: 'chevron-up',
      onSelect: () => {
        onMove(index - 1);
      },
    });
  }
  if (index < total - 1) {
    items.push({
      label: t('settings.moveDown', { name }),
      icon: 'chevron-down',
      onSelect: () => {
        onMove(index + 1);
      },
    });
  }
  return items;
}

/**
 * ⚠️⚠️ **THE DELETE LABEL CARRIES ITS OWN REFUSAL — the mockup's `'Törlés — előbb vedd le a
 * dolgozókról'`.** A disabled item saying only *Törlés* is a dead end somebody clicks twice; one
 * that says why is an instruction. The server refuses it too, so this is the explanation rather
 * than the guard.
 */
function deleteItem(
  t: Translate,
  used: number,
  blockedKey:
    'settings.skills.deleteHeld' | 'settings.skills.deleteFull' | 'settings.skills.deleteLast',
  onDelete: () => void,
): MenuItem {
  if (used > 0) {
    return { label: t(blockedKey), icon: 'trash', destructive: true, disabled: true };
  }
  return { label: t('common.delete'), icon: 'trash', destructive: true, onSelect: onDelete };
}

export function SkillsSection(props: SkillsSectionProps): React.JSX.Element {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const { skills, categories, levels, canManage } = props;
  const loading = props.loading === true;

  const [tab, setTab] = useState<Tab>('skills');
  /**
   * ⚠️⚠️ **THE TOOLBAR WAS MISSED ON THE FIRST PASS AND IS IN THE MOCKUP'S MARKUP.** Its
   * logic was in the same dump the rows came from — `wsSkillQ`, `wsSetSkillQ`,
   * `wsSkillCatFilterItems` — and the screen was built from the row builders alone. Nineteen
   * skills is already a list somebody scrolls; the filter is how the Frontend four are found.
   *
   * ⚠️ **Client-side, because the whole vocabulary is already loaded.** Every tab needs all
   * three lists to draw its counts, so there is nothing to fetch and a server round trip per
   * keystroke would be slower than the array.
   */
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [editingSkill, setEditingSkill] = useState<SkillDraft | null>(null);
  const [editingCategory, setEditingCategory] = useState<CategoryDraft | null>(null);
  const [editingLevel, setEditingLevel] = useState<LevelDraft | null>(null);

  /**
   * ⚠️ The mockup's own predicate: the name, the category NAME and the description, folded.
   * Matching the category's name rather than only the skill's is what makes *"front"* find four
   * rows whose own names do not contain it.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return skills.filter((skill) => {
      if (categoryFilter !== '' && skill.categoryId !== categoryFilter) return false;
      if (needle === '') return true;
      return `${skill.name} ${skill.categoryName} ${skill.description ?? ''}`
        .toLowerCase()
        .includes(needle);
    });
  }, [skills, query, categoryFilter]);

  /**
   * ⚠️⚠️ **THE TOOLBAR NOTE IS GONE, AND SO ARE THE ROW COUNTERS AND THE THREE HINT
   * PARAGRAPHS.** They said what the screen already shows: the list is nineteen rows long, the
   * tabs are named, and a row's category is in its own column. Each was one more sentence between
   * the reader and the table — named in the 21 Sep testing round, one by one.
   *
   * ⚠️ **The delete guards still explain themselves**, because a refusal has to: *"5 dolgozó
   * birtokolja"* is the reason the button did nothing, which is not something the table shows.
   */

  /**
   * ⚠️⚠️ **REORDERING IS OFF WHILE A FILTER IS ON, AND THAT IS NOT A LIMITATION — IT IS THE
   * ONLY HONEST ANSWER.** `reorder` sends a permutation of the WHOLE set (the server refuses
   * anything else), so dragging row 2 above row 1 of a four-row filtered view says nothing about
   * where they belong among nineteen. The grabber disappears rather than lying.
   */
  const filtered = query.trim() !== '' || categoryFilter !== '';
  const skillOrder = useDragOrder(
    shown.map((row) => row.id),
    canManage && !filtered ? props.onReorderSkills : undefined,
  );
  const categoryOrder = useDragOrder(
    categories.map((row) => row.id),
    canManage ? props.onReorderCategories : undefined,
  );
  const levelOrder = useDragOrder(
    levels.map((row) => row.id),
    canManage ? props.onReorderLevels : undefined,
  );

  const categoryOptions = categories.map((category) => ({
    value: category.id,
    label: category.name,
  }));

  /* ⚠️ The CTA's verb follows the tab — one button carrying three, the settings rule. */
  const newLabel =
    tab === 'categories'
      ? t('settings.skills.newCategory')
      : tab === 'levels'
        ? t('settings.skills.newLevel')
        : t('settings.skills.newSkill');

  const openNew = (): void => {
    /* ⚠️ A new row's starting colour is DATA — it is stored — so it comes from the shared
       palettes rather than a literal: the indigo swatch (the AA-safe step of the old `#5E6AD2`)
       for a category, and the blue status hue for a level. */
    if (tab === 'categories') {
      setEditingCategory({ id: null, name: '', color: DEFAULT_CATEGORY_COLOR, version: 0 });
      return;
    }
    if (tab === 'levels') {
      setEditingLevel({
        id: null,
        name: '',
        color: DEFAULT_LEVEL_COLOR,
        description: '',
        version: 0,
      });
      return;
    }
    setEditingSkill({
      id: null,
      name: '',
      /* ⚠️ The first category, because a skill REQUIRES one and an empty select is a dead form. */
      categoryId: categories[0]?.id ?? '',
      description: '',
      version: 0,
    });
  };

  const editSkill = (skill: Skill): void => {
    setEditingSkill({
      id: skill.id,
      name: skill.name,
      categoryId: skill.categoryId,
      description: skill.description ?? '',
      version: skill.version,
    });
  };
  const editCategory = (category: SkillCategory): void => {
    setEditingCategory({
      id: category.id,
      name: category.name,
      color: category.color,
      version: category.version,
    });
  };
  const editLevel = (level: SkillLevel): void => {
    setEditingLevel({
      id: level.id,
      name: level.name,
      color: level.color,
      description: level.description ?? '',
      version: level.version,
    });
  };

  /**
   * ⚠️⚠️ **THE SETTINGS TABLE, LIKE EVERY OTHER SETTINGS COLLECTION.** This screen drew its own
   * `<ul>` rows inside a titled card, so it was the one settings page whose rows, header and
   * toolbar sat at different sizes from their neighbours'. The cells are the same facts it drew:
   * the colour dot, the description as a second line, the category as plain text, the
   * *"nincs használatban"* note, and the row menu with its move items and explained deletes.
   */
  const unusedNote = (text: string) => ({
    kind: 'num' as const,
    text,
    className: 'text-muted-foreground text-[11.5px]',
  });

  const skillRows: SettingsRow[] = shown.map((skill, index) => ({
    id: skill.id,
    ...skillOrder.rowProps(skill.id),
    draggable: skillOrder.enabled,
    dragging: skillOrder.dragging === skill.id,
    over: skillOrder.over === skill.id,
    onOpen: !canManage
      ? undefined
      : () => {
          editSkill(skill);
        },
    cells: [
      skillOrder.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
      /* ⚠️ The dot is the CATEGORY's colour — a skill carries none of its own. */
      {
        kind: 'name' as const,
        text: skill.name,
        color: skill.categoryColor,
        ...(skill.description === null ? {} : { sub: skill.description }),
      },
      /* ⚠️ Plain text, as the markup has it — a Badge on every row of a nineteen-row list is
         nineteen outlined pills and a busier screen. */
      { kind: 'text' as const, text: skill.categoryName },
      unusedNote(skill.holderCount > 0 ? '' : t('settings.skills.heldByNobody')),
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: skill.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil' as const,
                onSelect: () => {
                  editSkill(skill);
                },
              },
              ...moveItems(t, skill.name, index, shown.length, (to) => {
                skillOrder.moveTo(skill.id, to);
              }),
              { type: 'separator' as const },
              deleteItem(t, skill.holderCount, 'settings.skills.deleteHeld', () => {
                props.onDeleteSkill(skill);
              }),
            ],
          }
        : { kind: 'blank' as const },
    ],
  }));

  const categoryRows: SettingsRow[] = categories.map((category, index) => ({
    id: category.id,
    ...categoryOrder.rowProps(category.id),
    draggable: categoryOrder.enabled,
    dragging: categoryOrder.dragging === category.id,
    over: categoryOrder.over === category.id,
    onOpen: !canManage
      ? undefined
      : () => {
          editCategory(category);
        },
    cells: [
      categoryOrder.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
      { kind: 'name' as const, text: category.name, color: category.color },
      unusedNote(category.skillCount > 0 ? '' : t('settings.skills.empty')),
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: category.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil' as const,
                onSelect: () => {
                  editCategory(category);
                },
              },
              ...moveItems(t, category.name, index, categories.length, (to) => {
                categoryOrder.moveTo(category.id, to);
              }),
              { type: 'separator' as const },
              /* ⚠️ Two different refusals: full, or the last one standing. */
              categories.length < 2
                ? {
                    label: t('settings.skills.deleteLast'),
                    icon: 'trash' as const,
                    destructive: true,
                    disabled: true,
                  }
                : deleteItem(t, category.skillCount, 'settings.skills.deleteFull', () => {
                    props.onDeleteCategory(category);
                  }),
            ],
          }
        : { kind: 'blank' as const },
    ],
  }));

  const levelRows: SettingsRow[] = levels.map((level, index) => ({
    id: level.id,
    ...levelOrder.rowProps(level.id),
    draggable: levelOrder.enabled,
    dragging: levelOrder.dragging === level.id,
    over: levelOrder.over === level.id,
    onOpen: !canManage
      ? undefined
      : () => {
          editLevel(level);
        },
    cells: [
      levelOrder.enabled ? { kind: 'drag' as const } : { kind: 'blank' as const },
      /* ⚠️⚠️ **THE RANK, BECAUSE THE POSITION IS THE WEIGHT.** Without it this tab reads as three
         lists of names and the drag handle looks cosmetic. It is the settings table's own `index`
         cell now — the row number IS the rank here — rather than a circle only this screen drew. */
      { kind: 'index' as const, text: level.rank },
      {
        kind: 'name' as const,
        text: level.name,
        color: level.color,
        ...(level.description === null ? {} : { sub: level.description }),
      },
      unusedNote(level.holderCount > 0 ? '' : t('settings.skills.heldByNobody')),
      canManage
        ? {
            kind: 'kebab' as const,
            ariaLabel: t('settings.rowMenu', { name: level.name }),
            items: [
              {
                label: t('common.edit'),
                icon: 'pencil' as const,
                onSelect: () => {
                  editLevel(level);
                },
              },
              ...moveItems(t, level.name, index, levels.length, (to) => {
                levelOrder.moveTo(level.id, to);
              }),
              { type: 'separator' as const },
              levels.length < 2
                ? {
                    label: t('settings.skills.deleteLast'),
                    icon: 'trash' as const,
                    destructive: true,
                    disabled: true,
                  }
                : deleteItem(t, level.holderCount, 'settings.skills.deleteHeld', () => {
                    props.onDeleteLevel(level);
                  }),
            ],
          }
        : { kind: 'blank' as const },
    ],
  }));

  /* ⚠️ The search and the category filter belong to the skills tab alone — categories and levels
     are short lists whose order is the point. */
  const toolbar =
    tab !== 'skills'
      ? {}
      : {
          search: query,
          onSearch: setQuery,
          searchPlaceholder: t('settings.skills.search'),
          filters: [
            {
              key: 'category',
              value: categoryFilter,
              ariaLabel: t('settings.skills.category'),
              options: [
                { value: '', label: t('settings.skills.allCategories') },
                ...categories.map((category) => ({
                  value: category.id,
                  label: category.name,
                  /* ⚠️ The mockup puts the category's own count on its filter item. */
                  hint: String(category.skillCount),
                })),
              ],
              onChange: setCategoryFilter,
            },
          ],
        };

  return (
    <div data-screen="settings-skills" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.skills')}
        action={canManage ? { label: newLabel, icon: 'plus', onSelect: openNew } : undefined}
        tabs={
          <Tabs
            variant="underline"
            className="border-0"
            label={t('settings.nav.skills')}
            value={tab}
            onValueChange={(value) => {
              setTab(
                value === 'categories' ? 'categories' : value === 'levels' ? 'levels' : 'skills',
              );
            }}
            items={[
              {
                value: 'skills',
                label: t('settings.skills.title'),
                icon: 'sparkles',
                ...(loading ? {} : { count: skills.length }),
              },
              {
                value: 'categories',
                label: t('settings.skills.categories'),
                icon: 'tag',
                ...(loading ? {} : { count: categories.length }),
              },
              {
                value: 'levels',
                label: t('settings.skills.levels'),
                icon: 'star',
                ...(loading ? {} : { count: levels.length }),
              },
            ]}
          />
        }
        error={props.serverError}
        {...toolbar}
      >
        {tab === 'skills' ? (
          <SettingsTable
            grid="26px minmax(0,1fr) 126px 120px 36px"
            minWidth={600}
            columns={[
              {},
              { label: t('settings.skills.name') },
              { label: t('settings.skills.category') },
              {},
              {},
            ]}
            groups={[{ rows: skillRows }]}
            loading={loading}
            empty={
              filtered
                ? {
                    icon: 'search',
                    title: t('settings.skills.noMatchTitle'),
                    body: t('settings.skills.noMatch'),
                  }
                : {
                    icon: 'sparkles',
                    title: t('settings.skills.emptySkills'),
                    body: t('settings.skills.emptySkillsBody'),
                  }
            }
          />
        ) : tab === 'categories' ? (
          <SettingsTable
            grid="26px minmax(0,1fr) 120px 36px"
            minWidth={520}
            columns={[{}, { label: t('settings.skills.name') }, {}, {}]}
            groups={[{ rows: categoryRows }]}
            loading={loading}
            empty={{
              icon: 'tag',
              title: t('settings.skills.emptyCategories'),
              body: t('settings.skills.emptyCategoriesBody'),
            }}
          />
        ) : (
          <SettingsTable
            grid="26px 24px minmax(0,1fr) 120px 36px"
            minWidth={520}
            columns={[{}, {}, { label: t('settings.skills.name') }, {}, {}]}
            groups={[{ rows: levelRows }]}
            loading={loading}
            empty={{
              icon: 'star',
              title: t('settings.skills.emptyLevels'),
              body: t('settings.skills.emptyLevelsBody'),
            }}
          />
        )}
      </SettingsPage>

      {editingSkill !== null && (
        <EditorDialog
          open
          title={
            editingSkill.id === null
              ? t('settings.skills.newSkill')
              : t('settings.skills.editSkill')
          }
          onClose={() => {
            setSaveFailed(false);
            setEditingSkill(null);
          }}
          busy={saving}
          error={saveFailed ? props.serverError : null}
          onSave={() => {
            setSaving(true);
            void props.onSaveSkill(editingSkill).then((landed) => {
              setSaving(false);
              setSaveFailed(!landed);
              if (landed) setEditingSkill(null);
            });
          }}
          saveDisabled={editingSkill.name.trim().length === 0 || editingSkill.categoryId === ''}
        >
          <Field label={t('settings.skills.name')}>
            <TextInput
              value={editingSkill.name}
              onChange={(event) => {
                setEditingSkill({ ...editingSkill, name: event.target.value });
              }}
            />
          </Field>
          <Field label={t('settings.skills.category')}>
            <Select
              value={editingSkill.categoryId}
              onChange={(event) => {
                setEditingSkill({ ...editingSkill, categoryId: event.target.value });
              }}
              options={categoryOptions}
            />
          </Field>
          <Field label={t('settings.skills.description')}>
            <TextInput
              value={editingSkill.description}
              onChange={(event) => {
                setEditingSkill({ ...editingSkill, description: event.target.value });
              }}
            />
          </Field>
        </EditorDialog>
      )}

      {editingCategory !== null && (
        <EditorDialog
          open
          title={
            editingCategory.id === null
              ? t('settings.skills.newCategory')
              : t('settings.skills.editCategory')
          }
          onClose={() => {
            setSaveFailed(false);
            setEditingCategory(null);
          }}
          busy={saving}
          error={saveFailed ? props.serverError : null}
          onSave={() => {
            setSaving(true);
            void props.onSaveCategory(editingCategory).then((landed) => {
              setSaving(false);
              setSaveFailed(!landed);
              if (landed) setEditingCategory(null);
            });
          }}
          saveDisabled={editingCategory.name.trim().length === 0}
        >
          <Field label={t('settings.skills.name')}>
            <TextInput
              value={editingCategory.name}
              onChange={(event) => {
                setEditingCategory({ ...editingCategory, name: event.target.value });
              }}
            />
          </Field>
          <ColorPicker
            label={t('settings.skills.color')}
            value={editingCategory.color}
            onChange={(color) => {
              setEditingCategory({ ...editingCategory, color });
            }}
          />
        </EditorDialog>
      )}

      {editingLevel !== null && (
        <EditorDialog
          open
          title={
            editingLevel.id === null
              ? t('settings.skills.newLevel')
              : t('settings.skills.editLevel')
          }
          onClose={() => {
            setSaveFailed(false);
            setEditingLevel(null);
          }}
          busy={saving}
          error={saveFailed ? props.serverError : null}
          onSave={() => {
            setSaving(true);
            void props.onSaveLevel(editingLevel).then((landed) => {
              setSaving(false);
              setSaveFailed(!landed);
              if (landed) setEditingLevel(null);
            });
          }}
          saveDisabled={editingLevel.name.trim().length === 0}
        >
          <Field label={t('settings.skills.name')}>
            <TextInput
              value={editingLevel.name}
              onChange={(event) => {
                setEditingLevel({ ...editingLevel, name: event.target.value });
              }}
            />
          </Field>
          <ColorPicker
            label={t('settings.skills.color')}
            value={editingLevel.color}
            onChange={(color) => {
              setEditingLevel({ ...editingLevel, color });
            }}
          />
          <Field label={t('settings.skills.description')}>
            <TextInput
              value={editingLevel.description}
              onChange={(event) => {
                setEditingLevel({ ...editingLevel, description: event.target.value });
              }}
            />
          </Field>
        </EditorDialog>
      )}
    </div>
  );
}
